import {ChevronLeftIcon, ChevronRightIcon, CompassIcon, XIcon} from "lucide-react";
import {useEffect, useMemo, useState} from "react";
import {createPortal} from "react-dom";

import {Button} from "@components/ui/button";
import {useChatContext} from "@contexts/ChatContext";

export interface WorkspaceTourStep {
    id: string;
    tab?: string;
    selector: string;
    extraSelectors?: string[];
    requiresAgentPanel?: boolean;
    title: string;
    body: string;
    badge: string;
    placement?: "top" | "bottom";
}

interface WorkspaceTourProps {
    open: boolean;
    activeTab: string;
    onClose: () => void;
    onTabChange: (tab: string) => void;
    steps: WorkspaceTourStep[];
    dialogLabel: string;
    returnTabOnClose?: string;
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

export function WorkspaceTour({
    open,
    activeTab,
    onClose,
    onTabChange,
    steps,
    dialogLabel,
    returnTabOnClose,
}: WorkspaceTourProps) {
    const {setChatDocked, setSidePanelOpen, setChatFullscreen} = useChatContext();
    const [stepIndex, setStepIndex] = useState(0);
    const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
    const [targetVisible, setTargetVisible] = useState(false);

    const currentStep = steps[Math.min(stepIndex, Math.max(steps.length - 1, 0))];

    const targetSelectors = useMemo(
        () => currentStep ? [currentStep.selector, ...(currentStep.extraSelectors ?? [])] : [],
        [currentStep],
    );

    useEffect(() => {
        if (!open) return;
        setStepIndex(0);
    }, [open]);

    useEffect(() => {
        if (!open || !currentStep?.tab) return;
        if (activeTab !== currentStep.tab) onTabChange(currentStep.tab);
    }, [activeTab, currentStep, onTabChange, open]);

    useEffect(() => {
        if (!open || !currentStep?.requiresAgentPanel) return;
        setChatDocked(true);
        setChatFullscreen(false);
        setSidePanelOpen(true);
    }, [currentStep, open, setChatDocked, setChatFullscreen, setSidePanelOpen]);

    useEffect(() => {
        if (!open || !currentStep) return;

        let cancelled = false;
        let timeoutId: number | null = null;
        let attempts = 0;

        setTargetRect(null);
        setTargetVisible(false);

        const updateTarget = () => {
            if (cancelled) return;
            const elements = targetSelectors
                .map((selector) => document.querySelector<HTMLElement>(selector))
                .filter((element): element is HTMLElement => Boolean(element));

            if (elements.length === 0) {
                if (attempts < 30) {
                    attempts += 1;
                    timeoutId = window.setTimeout(updateTarget, 120);
                }
                return;
            }

            elements[0].scrollIntoView({behavior: "smooth", block: "start", inline: "nearest"});

            const rects = elements
                .map((element) => element.getBoundingClientRect())
                .filter((rect) => rect.width > 0 && rect.height > 0);

            if (rects.length > 0) {
                const top = Math.min(...rects.map((rect) => rect.top));
                const left = Math.min(...rects.map((rect) => rect.left));
                const right = Math.max(...rects.map((rect) => rect.right));
                const bottom = Math.max(...rects.map((rect) => rect.bottom));
                setTargetRect(new DOMRect(left, top, right - left, bottom - top));
                setTargetVisible(true);
                return;
            }

            if (attempts < 30) {
                attempts += 1;
                timeoutId = window.setTimeout(updateTarget, 120);
            }
        };

        timeoutId = window.setTimeout(
            updateTarget,
            currentStep.requiresAgentPanel ? 280 : !currentStep.tab || activeTab === currentStep.tab ? 80 : 220,
        );

        return () => {
            cancelled = true;
            if (timeoutId != null) window.clearTimeout(timeoutId);
        };
    }, [activeTab, currentStep, open, targetSelectors]);

    useEffect(() => {
        if (!open || !currentStep) return;

        const refreshRect = () => {
            const elements = targetSelectors
                .map((selector) => document.querySelector<HTMLElement>(selector))
                .filter((element): element is HTMLElement => Boolean(element));
            if (elements.length === 0) return;

            const rects = elements
                .map((element) => element.getBoundingClientRect())
                .filter((rect) => rect.width > 0 && rect.height > 0);

            if (rects.length > 0) {
                const top = Math.min(...rects.map((rect) => rect.top));
                const left = Math.min(...rects.map((rect) => rect.left));
                const right = Math.max(...rects.map((rect) => rect.right));
                const bottom = Math.max(...rects.map((rect) => rect.bottom));
                setTargetRect(new DOMRect(left, top, right - left, bottom - top));
                setTargetVisible(true);
            }
        };

        window.addEventListener("resize", refreshRect);
        window.addEventListener("scroll", refreshRect, true);
        return () => {
            window.removeEventListener("resize", refreshRect);
            window.removeEventListener("scroll", refreshRect, true);
        };
    }, [currentStep, open, targetSelectors]);

    useEffect(() => {
        if (!open || steps.length === 0) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
            if (event.key === "ArrowRight") setStepIndex((index) => Math.min(index + 1, steps.length - 1));
            if (event.key === "ArrowLeft") setStepIndex((index) => Math.max(index - 1, 0));
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [onClose, open, steps.length]);

    const highlightStyle = useMemo(() => {
        if (!targetRect) return null;
        const viewportPadding = 8;
        const visibleTop = clamp(targetRect.top - 10, viewportPadding, window.innerHeight - viewportPadding);
        const visibleLeft = clamp(targetRect.left - 10, viewportPadding, window.innerWidth - viewportPadding);
        const visibleBottom = clamp(targetRect.bottom + 10, viewportPadding, window.innerHeight - viewportPadding);
        const visibleRight = clamp(targetRect.right + 10, viewportPadding, window.innerWidth - viewportPadding);

        if (visibleBottom <= visibleTop || visibleRight <= visibleLeft) return null;

        return {
            top: visibleTop,
            left: visibleLeft,
            width: visibleRight - visibleLeft,
            height: visibleBottom - visibleTop,
        };
    }, [targetRect]);

    const cardStyle = useMemo(() => {
        const width = Math.min(360, window.innerWidth - 32);
        const maxCardHeight = Math.max(240, window.innerHeight - 32);
        const estimatedCardHeight = Math.min(320, maxCardHeight);
        if (!currentStep || !targetRect || !highlightStyle || !targetVisible) {
            return {
                top: Math.max((window.innerHeight - estimatedCardHeight) / 2, 24),
                left: Math.max((window.innerWidth - width) / 2, 16),
                width,
            };
        }

        const canDockBeside = highlightStyle.height > window.innerHeight * 0.55 && highlightStyle.width < window.innerWidth * 0.4;
        if (canDockBeside) {
            const rightSpace = window.innerWidth - (highlightStyle.left + highlightStyle.width) - 16;
            if (rightSpace >= width + 18) {
                return {
                    top: clamp(highlightStyle.top + 12, 16, Math.max(16, window.innerHeight - estimatedCardHeight - 16)),
                    left: clamp(highlightStyle.left + highlightStyle.width + 18, 16, window.innerWidth - width - 16),
                    width,
                };
            }

            const leftSpace = highlightStyle.left - 16;
            if (leftSpace >= width + 18) {
                return {
                    top: clamp(highlightStyle.top + 12, 16, Math.max(16, window.innerHeight - estimatedCardHeight - 16)),
                    left: clamp(highlightStyle.left - width - 18, 16, window.innerWidth - width - 16),
                    width,
                };
            }
        }

        const nextTop =
            currentStep.placement === "top" && highlightStyle.top > 240
                ? highlightStyle.top - 220
                : highlightStyle.top + highlightStyle.height + 18;

        return {
            top: clamp(nextTop, 16, Math.max(16, window.innerHeight - estimatedCardHeight - 16)),
            left: clamp(highlightStyle.left + highlightStyle.width / 2 - width / 2, 16, window.innerWidth - width - 16),
            width,
        };
    }, [currentStep, highlightStyle, targetRect, targetVisible]);

    if (!open || !currentStep || steps.length === 0 || typeof document === "undefined") return null;

    const isLastStep = stepIndex === steps.length - 1;
    const handleClose = () => {
        if (returnTabOnClose && activeTab !== returnTabOnClose) {
            onTabChange(returnTabOnClose);
        }
        onClose();
    };

    return createPortal(
        <div className="fixed inset-0 z-[1200]">
            <button
                type="button"
                className="absolute inset-0 cursor-default bg-transparent"
                aria-label={`Close ${dialogLabel}`}
                onClick={handleClose}
            />

            {highlightStyle ? (
                <div
                    className="pointer-events-none fixed rounded-3xl border-2 border-[#5BF6C8] bg-transparent transition-all duration-300"
                    style={{
                        ...highlightStyle,
                        boxShadow: "0 0 0 9999px rgba(15, 23, 42, 0.72), 0 0 0 8px rgba(32, 157, 179, 0.14)",
                    }}
                />
            ) : (
                <div className="pointer-events-none absolute inset-0 bg-slate-950/72" />
            )}

            <div
                className="fixed overflow-y-auto rounded-3xl border border-[#BFEFE7] bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,0.30)]"
                style={cardStyle}
                role="dialog"
                aria-modal="true"
                aria-label={dialogLabel}
            >
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center rounded-full bg-[#E6FBF6] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[1.6px] text-[#127A8A]">
                            {currentStep.badge}
                        </span>
                        <span className="text-xs font-medium text-slate-500">
                            {stepIndex + 1} of {steps.length}
                        </span>
                    </div>
                    <button
                        type="button"
                        className="rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                        aria-label={`Close ${dialogLabel}`}
                        onClick={handleClose}
                    >
                        <XIcon className="size-4" />
                    </button>
                </div>

                <div className="mt-4 flex items-start gap-3">
                    <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-2xl bg-[#209DB3] text-white">
                        <CompassIcon className="size-4" />
                    </div>
                    <div className="min-w-0">
                        <h3 className="text-base font-semibold text-slate-900">{currentStep.title}</h3>
                        <p className="mt-2 text-sm leading-6 text-slate-600">{currentStep.body}</p>
                        {!targetVisible && (
                            <p className="mt-2 text-xs text-slate-500">
                                Waiting for this section to load on the current tab...
                            </p>
                        )}
                    </div>
                </div>

                <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div
                        className="h-full rounded-full bg-[#209DB3] transition-all duration-300"
                        style={{width: `${((stepIndex + 1) / steps.length) * 100}%`}}
                    />
                </div>

                <div className="mt-5 flex items-center justify-between gap-3">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setStepIndex((index) => Math.max(index - 1, 0))}
                        disabled={stepIndex === 0}
                    >
                        <ChevronLeftIcon className="mr-1 size-4" />
                        Back
                    </Button>

                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={handleClose}>
                            Skip
                        </Button>
                        <Button
                            size="sm"
                            className="bg-[#209DB3] text-white hover:bg-[#167B8E]"
                            onClick={() => {
                                if (isLastStep) {
                                    handleClose();
                                    return;
                                }
                                setStepIndex((index) => Math.min(index + 1, steps.length - 1));
                            }}
                        >
                            {isLastStep ? "Done" : "Next"}
                            {!isLastStep && <ChevronRightIcon className="ml-1 size-4" />}
                        </Button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}
